import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LOGO_URL, api } from "@/lib/api";
import {
  FilePlus2, Files, FileText, Users, Package, LogOut, Menu,
  Settings as SettingsIcon, LayoutDashboard, Receipt, Mail, CalendarCheck, X, Truck,
  ClipboardList, SlidersHorizontal,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";

const mainItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/create", label: "Create an Order", icon: FilePlus2, testid: "nav-create-invoice" },
  { to: "/orders", label: "Orders", icon: ClipboardList, testid: "nav-orders" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/delivery", label: "Delivery", icon: Truck, testid: "nav-delivery" },
  { to: "/accounts", label: "Accounts", icon: Users, testid: "nav-accounts" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses" },
];

const settingsChildren = [
  { to: "/settings", label: "Admin Settings", icon: SlidersHorizontal, testid: "nav-settings" },
  { to: "/email-templates", label: "Email Templates", icon: Mail, testid: "nav-email-templates" },
];

function NavItems({ onNavigate }) {
  const location = useLocation();
  const inSettings = location.pathname === "/settings" || location.pathname === "/email-templates";

  return (
    <>
      {mainItems.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={it.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md transition-colors text-sm font-medium ${
                isActive
                  ? "bg-white text-[#032352]"
                  : "text-white/80 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon className="w-5 h-5" />
            {it.label}
          </NavLink>
        );
      })}

      {/* Settings group */}
      <div data-testid="nav-settings-group">
        <div className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium ${inSettings ? "text-white" : "text-white/60"}`}>
          <SettingsIcon className="w-5 h-5" />
          Settings
        </div>
        <div className="ml-4 space-y-0.5">
          {settingsChildren.map((ch) => {
            const Icon = ch.icon;
            return (
              <NavLink
                key={ch.to}
                to={ch.to}
                data-testid={ch.testid}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors text-sm ${
                    isActive
                      ? "bg-white text-[#032352] font-medium"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {ch.label}
              </NavLink>
            );
          })}
        </div>
      </div>
    </>
  );
}

// Key: jl_stmts_sent_YYYY-MM — set when statements are sent this month
// Key: jl_stmts_dismissed_YYYY-MM — set when banner dismissed for session
function bannerMonthKey(prefix) {
  const d = new Date();
  return `${prefix}_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function StatementsBanner() {
  const today = new Date().getDate();
  const [visible, setVisible] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (today !== 1) return;
    const sent = localStorage.getItem(bannerMonthKey("jl_stmts_sent"));
    const dismissed = sessionStorage.getItem(bannerMonthKey("jl_stmts_dismissed"));
    if (!sent && !dismissed) setVisible(true);
  }, [today]);

  const dismiss = () => {
    sessionStorage.setItem(bannerMonthKey("jl_stmts_dismissed"), "1");
    setVisible(false);
  };

  const sendStatements = useCallback(async () => {
    setSending(true);
    try {
      const { data } = await api.post("/statements/send-monthly");
      const { sent, skipped_no_email } = data;
      localStorage.setItem(bannerMonthKey("jl_stmts_sent"), "1");
      setVisible(false);
      if (sent === 0) {
        toast.info("No qualifying customers to send statements to this month.");
      } else {
        toast.success(
          `Statements sent to ${sent} customer${sent !== 1 ? "s" : ""}!` +
          (skipped_no_email?.length ? ` (${skipped_no_email.length} skipped — no email on file)` : "")
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send statements");
    } finally {
      setSending(false);
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="statements-banner"
      className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-4 flex-wrap"
    >
      <div className="flex items-center gap-3">
        <CalendarCheck className="w-5 h-5 text-amber-600 shrink-0" />
        <p className="text-sm font-medium text-amber-900">
          It&apos;s the 1st of the month — Send Statements?
          <span className="ml-2 text-amber-700 font-normal text-xs">
            Only customers with a balance or activity this month will receive one.
          </span>
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          data-testid="send-statements-button"
          onClick={sendStatements}
          disabled={sending}
          className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-1.5 rounded-md transition-colors disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send Statements"}
        </button>
        <button
          data-testid="dismiss-banner-button"
          onClick={dismiss}
          className="text-amber-600 hover:text-amber-800 p-1.5 rounded-md hover:bg-amber-100 transition-colors"
          title="Dismiss for this session"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children, onLock }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 bg-[#032352] text-white flex-col fixed h-screen z-30">
        <div className="p-6 border-b border-white/10">
          <div className="bg-white rounded-lg p-3 flex items-center justify-center">
            <img src={LOGO_URL} alt="J&L" className="h-16 object-contain" />
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <NavItems />
        </nav>
        <div className="p-4 border-t border-white/10">
          <button
            onClick={onLock}
            data-testid="lock-app-button"
            className="flex items-center gap-3 px-4 py-3 rounded-md w-full text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Lock
          </button>
        </div>
      </aside>

      {/* Mobile top bar — safe-area-inset-top pushes content below iOS status bar */}
      <header
        className="md:hidden sticky top-0 z-30 bg-[#032352]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center justify-between px-4 py-4">
          <div className="bg-white rounded-lg px-3 py-2 flex items-center">
            <img src={LOGO_URL} alt="J&L" className="h-14 object-contain" />
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                className="text-white p-2.5 rounded-md hover:bg-white/10 transition-colors"
              >
                <Menu className="w-8 h-8" />
              </button>
            </SheetTrigger>
          <SheetContent side="left" className="w-72 bg-[#032352] border-none p-0 flex flex-col">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="p-6 border-b border-white/10">
              <div className="bg-white rounded-lg p-3 flex items-center justify-center">
                <img src={LOGO_URL} alt="J&L" className="h-16 object-contain" />
              </div>
            </div>
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              <NavItems onNavigate={() => setOpen(false)} />
            </nav>
            <div className="p-4 border-t border-white/10">
              <button
                onClick={() => { setOpen(false); onLock(); }}
                data-testid="lock-app-button-mobile"
                className="flex items-center gap-3 px-4 py-3 rounded-md w-full text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Lock
              </button>
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </header>

      <div className="md:ml-64 flex flex-col min-h-screen">
        <StatementsBanner />
        <main className="flex-1 p-4 sm:p-6 md:p-8">{children}</main>
      </div>
    </div>
  );
}
